import { Theme } from '@material-ui/core';

import type { PortBoundary, ViewState } from '../types/warehouse.types';
import { drawPolygon, drawRect, drawText } from '../utils/canvasDrawing';
import { BaseRenderer } from './base/BaseRenderer';

export interface PortRendererConfig {
  borderLineWidth: number;
  showLabel: boolean;
  labelPadding: number;
  // Hover config
  hoverBorderLineWidth: number;
  hoverBorderColor: string;
  // Selection config
  selectedBorderLineWidth: number;
  selectedBorderColor: string;
  selectedGlowIntensity: number; // Multiplier for shadow blur
}

const defaultConfig: PortRendererConfig = {
  borderLineWidth: 3,
  showLabel: true,
  labelPadding: 4,
  hoverBorderLineWidth: 5,
  hoverBorderColor: '', // Will use theme.palette.secondary.main
  selectedBorderLineWidth: 6,
  selectedBorderColor: '', // Will use theme.palette.primary.main
  selectedGlowIntensity: 2,
};

export class PortRenderer extends BaseRenderer<PortRendererConfig> {
  constructor(theme: Theme, config?: Partial<PortRendererConfig>) {
    super(theme, defaultConfig, config);
  }

  render(
    ctx: CanvasRenderingContext2D,
    ports: PortBoundary[],
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    hoveredPort: PortBoundary | null = null,
    selectedPortNames: Set<string> = new Set()
  ): void {
    const {
      borderLineWidth,
      showLabel,
      labelPadding,
      hoverBorderLineWidth,
      hoverBorderColor,
      selectedBorderLineWidth,
      selectedBorderColor,
      selectedGlowIntensity
    } = this.config;

    ports.forEach((port) => {
      if (port.polygon.length < 2) return;

      // Convert world coordinates to screen coordinates
      const screenPoints = port.polygon.map(point => worldToScreen(point.x, point.y));

      // Determine state
      const isBlocked = port.blocked === true;
      const isSelected = selectedPortNames.has(port.name);
      const isHovered = hoveredPort === port;

      // Determine border styling
      let strokeColor: string;
      let strokeWidth: number;

      if (isBlocked) {
        // Use blocked color from theme
        strokeColor = this.theme.palette.storageUi.blocked;
        strokeWidth = port.borderSize || borderLineWidth;
      } else if (isSelected) {
        strokeColor = selectedBorderColor || this.theme.palette.primary.main;
        strokeWidth = selectedBorderLineWidth;
      } else if (isHovered) {
        strokeColor = hoverBorderColor || this.theme.palette.secondary.main;
        strokeWidth = hoverBorderLineWidth;
      } else {
        strokeColor = port.borderColor;
        strokeWidth = port.borderSize || borderLineWidth;
      }

      // Add glow effect for selected ports (but not if blocked)
      if (isSelected && !isBlocked) {
        ctx.shadowColor = strokeColor;
        ctx.shadowBlur = strokeWidth * selectedGlowIntensity;
      }

      // Draw port line/polygon
      drawPolygon(ctx, screenPoints, {
        stroke: true,
        fill: false,
        closePath: screenPoints.length > 2, // Close if it's a polygon
        style: {
          strokeStyle: strokeColor,
          lineWidth: strokeWidth,
          lineCap: 'round',
          lineJoin: 'round'
        }
      });

      // Reset shadow
      ctx.shadowBlur = 0;

      // Draw label if enabled
      if (showLabel) {
        this.renderLabel(ctx, port, viewState, worldToScreen, isSelected, isHovered, isBlocked, labelPadding);
      }
    });
  }

  /**
   * Render port label
   */
  private renderLabel(
    ctx: CanvasRenderingContext2D,
    port: PortBoundary,
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    isSelected: boolean,
    isHovered: boolean,
    isBlocked: boolean,
    labelPadding: number
  ): void {
    if (port.polygon.length < 2) return;
    const fontFamily = this.theme.typography.fontFamily;

    // Calculate midpoint of the port
    const midX = port.polygon.reduce((sum, p) => sum + p.x, 0) / port.polygon.length;
    const midY = port.polygon.reduce((sum, p) => sum + p.y, 0) / port.polygon.length;
    const midScreenPos = worldToScreen(midX, midY);

    // Calculate font size based on scale
    const baseFontSize = isSelected ? 16 : isHovered ? 15 : 14;
    const fontSize = Math.max(10, Math.min(18, baseFontSize * viewState.scale * 5));

    ctx.font = `bold ${fontSize}px ${fontFamily}`;
    const textMetrics = ctx.measureText(port.name);
    const textWidth = textMetrics.width;
    const textHeight = fontSize;

    // Background color based on state
    let bgColor = this.theme.palette.storageUi.backgroundSecondary + '99';
    let textColor = this.theme.palette.storageUi.textPrimary + '66';

    if (isBlocked) {
      bgColor = this.theme.palette.storageUi.blocked + 'CC';
      textColor = this.theme.palette.storageUi.backgroundPrimary;
    } else if (isSelected) {
      bgColor = this.theme.palette.primary.main + 'CC';
      textColor = this.theme.palette.primary.contrastText;
    } else if (isHovered) {
      bgColor = this.theme.palette.secondary.main + 'AA';
      textColor = this.theme.palette.secondary.contrastText;
    }

    // Draw background
    const padding = isSelected ? labelPadding + 2 : labelPadding;
    drawRect(
      ctx,
      midScreenPos.x - textWidth / 2 - padding,
      midScreenPos.y - textHeight / 2 - padding,
      textWidth + padding * 2,
      textHeight + padding * 2,
      {
        fill: true,
        stroke: isSelected || isHovered,
        style: {
          fillStyle: bgColor,
          strokeStyle: isSelected 
            ? this.theme.palette.primary.main 
            : this.theme.palette.secondary.main,
          lineWidth: isSelected ? 2 : 1
        }
      }
    );

    // Draw text
    drawText(ctx, port.name, midScreenPos, {
      fill: true,
      stroke: false,
      style: {
        fillStyle: textColor,
        textAlign: 'center',
        textBaseline: 'middle',
        font: `bold ${fontSize}px ${fontFamily}`
      }
    });
  }
}
